import { useNavigate } from "react-router-dom";
import './editor-page-toolbar.scss';

import { Badge, BadgeType } from '../../../badge/badge';
import IconBack from '../../../icons/icon-back/icon-back';
import IconSave from '../../../icons/icon-save/icon-save';


import LocalesSelector from '../../../../translation/locales-selector/locales-selector';
import { ButtonIcon } from '../../../inputs/button-icon/button-icon';
import LinearLoader from "../../../loaders/linear-loader/linear-loader";
import ExpandCollapseAll from "./expand-collapse-all/expand-collapse-all";
import usePage from "../../base-page-layout/page-hook";
import { useResizeDetector } from 'react-resize-detector';
import React, { useEffect, useRef } from "react";
import { TOOLTIP_VARIANTS, Tooltip } from "../../../tooltip/tooltip";
import Icon from "../../../icons/icon/icon";
import IconInfo from "../../../icons/icon-info/icon-info";
import FontBody2 from "../../../fonts/font-body2/font-body2";
import { TOOLTIP_PLACEMENTS } from "../../../tooltip/tooltip-box";
import { FONT_COLOR } from "../../../fonts/font-props";
import { isSet } from "../../../../models/model-types-methods";

export interface EditorPageToolbarProps {
    hasBack?: boolean;
    hasLocalesSelector?: boolean;
    saveButton?: {
        hasChanges?: boolean;
        disabled?: boolean;
        onClick?: () => void;
        isValid?: boolean;
    },
    expandAllButton?: {
        expanded: boolean;
        onClick?: () => void;
    },
    content?: React.ReactNode;
    isLoading?: boolean;
}


const EditorPageToolbar = ({ hasBack = true, hasLocalesSelector = true, saveButton, expandAllButton, content, isLoading = false }: EditorPageToolbarProps) => {
    const toolbarRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { setStickyHeadersHeight, unsetStickyHeadersHeight, getTitleHeight } = usePage();
    const toolbarTop = getTitleHeight() + 'px';

    useResizeDetector<HTMLDivElement>({
        targetRef: toolbarRef,
        onResize: ({ height }) => {
            if (isSet(height)) {
                setStickyHeadersHeight(toolbarRef.current, height);
            }
        },
    });

    useEffect(() => {
        const ref = toolbarRef.current;
        return () => unsetStickyHeadersHeight(ref);
    }, [unsetStickyHeadersHeight])

    const InvalidInfoComponent = () => <IconInfo color="var(--color-accent-3)" />;

    return(
        <div className="editor-page-toolbar-sticky" style={{ top: toolbarTop }} ref={toolbarRef}>
            <div className="editor-page-toolbar">
                {hasBack && <div className="back-container">
                    <ButtonIcon Component={IconBack} onClick={() => navigate(-1)} />
                </div>}

                {hasLocalesSelector && <div className="locales-container">
                    <LocalesSelector />
                </div>}

                <div className="content-container">
                    {content}
                </div>

                {expandAllButton && (
                    <ExpandCollapseAll
                        isExpanded={expandAllButton.expanded}
                        onClick={expandAllButton.onClick}
                    />
                )}
                {saveButton && <div className="save-container">
                    {saveButton.hasChanges && <Badge text="unsaved" type={ BadgeType.WARNING } />}
                    <ButtonIcon disabled={saveButton.disabled} Component={IconSave} onClick={saveButton.onClick} />
                    {saveButton.isValid === false &&
                        <Tooltip content={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                Missing required information. <br/>
                                Please expand all sections and  <br/>
                                fill in the required fields.
                            </FontBody2>
                        } placement={TOOLTIP_PLACEMENTS.LEFT} variant={TOOLTIP_VARIANTS.ERROR}>
                            <Icon Component={InvalidInfoComponent} />
                        </Tooltip>
                    }
                </div>}

                {isLoading && <LinearLoader />}
            </div>
        </div>
    )
};

export default EditorPageToolbar;
